import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { Button, Col, Form, Row, Spinner, Card, Table, Badge } from 'react-bootstrap';
import { format } from 'date-fns';

const CouponUsageSummary = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);

    const accessToken = localStorage.getItem("authToken");
    const [outlets, setOutlets] = useState([]);
    const [outletName, setOutletName] = useState('');
    const [couponData, setCouponData] = useState([]);
    const [SubBtnLoading, setSubBtnLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [reportDate, setReportDate] = useState('');

    const months = [
        { value: 1, name: 'January' },
        { value: 2, name: 'February' },
        { value: 3, name: 'March' },
        { value: 4, name: 'April' },
        { value: 5, name: 'May' },
        { value: 6, name: 'June' },
        { value: 7, name: 'July' },
        { value: 8, name: 'August' },
        { value: 9, name: 'September' },
        { value: 10, name: 'October' },
        { value: 11, name: 'November' },
        { value: 12, name: 'December' }
    ];

    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

    const getOutlets = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("outlet", { headers });
        
            if (response?.data?.status === true) {
                let filteredOutlets = response?.data?.data?.outlets?.filter((act) => act?.status === "ACTIVE");
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const getCouponUsageSummary = async (month, year, outletId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            
            let newUrl = `summary/month-coupon-usage-summary?month=${month}&year=${year}`;
            
            if (outletId) {
                newUrl += `&outletId=${outletId}`;
                const selectedOutlet = outlets.find(outlet => outlet._id === outletId);
                setOutletName(selectedOutlet?.name || '');
            }
            
            const response = await axios.get(newUrl, { headers });
            
            if (response?.data?.status === true) {
                setCouponData(response?.data?.data?.reportData || []);
                setReportDate(`${months[month-1]?.name} ${year}`);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };
    
    const handleSubmit = async() => {
        setSubBtnLoading(true);
        await getCouponUsageSummary(selectedMonth, selectedYear, selectedOutlet);
        setSubBtnLoading(false);
    }

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getTotalDiscount = () => {
        return couponData.reduce((sum, coupon) => sum + (coupon.totalDiscount || 0), 0);
    };

    const getTotalSalesContribution = () => {
        return couponData.reduce((sum, coupon) => sum + (coupon.salesContribution || 0), 0);
    };

    const getTotalUsageCount = () => {
        return couponData.reduce((sum, coupon) => sum + (coupon.usageCount || 0), 0);
    };

    useEffect(() => {
        getOutlets();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                <div className="d-flex flex-column gap-3">
                    <div className={`card ${skin === "dark" ? "bg-dark-dark" : ""} border-0 shadow-sm`}>
                        <div className="card-header bg-white border-0 py-3">
                            <h4 className="mb-0 fw-bold">
                                <i className="bi bi-ticket-perforated me-2"></i>COUPON USAGE SUMMARY
                            </h4>
                        </div>
                        <hr className="mt-0 mb-0" />
                
                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="filters-section p-3 bg-light rounded-3 mb-4">
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Outlet
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedOutlet}
                                                onChange={(e) => setSelectedOutlet(e.target.value)}
                                                className="border-secondary"
                                            >
                                                <option value="">All Outlets</option>
                                                {outlets.map((outName, index) => (
                                                    <option key={index} value={outName?._id}>
                                                        {outName?.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="monthSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Month
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedMonth}
                                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                                className="border-secondary"
                                            >
                                                {months.map((month, index) => (
                                                    <option key={index} value={month.value}>
                                                        {month.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="yearSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Year
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                                className="border-secondary"
                                            >
                                                {years.map((year, index) => (
                                                    <option key={index} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            onClick={handleSubmit}
                                            disabled={SubBtnLoading || !selectedMonth || !selectedYear}
                                        >
                                            {SubBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Report Section */}
                            {couponData.length > 0 && (
                                <div className={`report-section ${skin === "dark" ? "text-light" : ""}`}>
                                    <Card className="mb-4 border-0 shadow-sm">
                                        <Card.Header className="bg-primary text-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="mb-0">Coupon Performance Report</h5>
                                                <div className="text-end">
                                                    {outletName && <small className="d-block">{outletName}</small>}
                                                    <small className="d-block">{reportDate}</small>
                                                </div>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-4">
                                                <Row>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Total Coupons Used:</span>
                                                                    <span className="fw-bold">{getTotalUsageCount()}</span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Total Discount Given:</span>
                                                                    <span className="fw-bold text-danger">-{formatCurrency(getTotalDiscount())}</span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Card className="border-0 shadow-sm bg-light">
                                                            <Card.Body className="py-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="fw-semibold">Sales Contribution:</span>
                                                                    <span className="fw-bold text-success">{formatCurrency(getTotalSalesContribution())}</span>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className="table-responsive">
                                                <Table bordered hover className="mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Coupon Name</th>
                                                            <th className="text-end">Usage Count</th>
                                                            <th className="text-end">Avg. Discount</th>
                                                            <th className="text-end">Total Discount</th>
                                                            <th className="text-end">Sales Contribution</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {couponData.map((coupon, index) => (
                                                            <tr key={coupon.couponId || index}>
                                                                <td>{index + 1}</td>
                                                                <td className="fw-semibold">{coupon.couponName}</td>
                                                                <td className="text-end">{coupon.usageCount}</td>
                                                                <td className="text-end">{formatCurrency(coupon.averageDiscount)}</td>
                                                                <td className="text-end text-danger">-{formatCurrency(coupon.totalDiscount)}</td>
                                                                <td className="text-end text-success">{formatCurrency(coupon.salesContribution)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="table-active">
                                                        <tr>
                                                            <td colSpan="2" className="fw-bold text-end">Totals:</td>
                                                            <td className="text-end fw-bold">{getTotalUsageCount()}</td>
                                                            <td className="text-end fw-bold">
                                                                {formatCurrency(getTotalDiscount() / (getTotalUsageCount() || 1))}
                                                            </td>
                                                            <td className="text-end fw-bold text-danger">-{formatCurrency(getTotalDiscount())}</td>
                                                            <td className="text-end fw-bold text-success">{formatCurrency(getTotalSalesContribution())}</td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>

                                            {/* <div className="mt-4">
                                                <h6 className="mb-3 fw-semibold">Performance Insights</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <Card className="border-0 shadow-sm h-100">
                                                            <Card.Header className="bg-light">
                                                                <h6 className="mb-0 fw-semibold">Top Performing Coupons</h6>
                                                            </Card.Header>
                                                            <Card.Body>
                                                                {[...couponData]
                                                                    .sort((a, b) => b.usageCount - a.usageCount)
                                                                    .slice(0, 3)
                                                                    .map((coupon, index) => (
                                                                        <div key={index} className="mb-2">
                                                                            <div className="d-flex justify-content-between">
                                                                                <span className="fw-semibold">{coupon.couponName}</span>
                                                                                <span>{coupon.usageCount} uses</span>
                                                                            </div>
                                                                            <div className="progress mt-1" style={{ height: '8px' }}>
                                                                                <div 
                                                                                    className="progress-bar bg-success" 
                                                                                    role="progressbar" 
                                                                                    style={{ width: `${(coupon.usageCount / getTotalUsageCount()) * 100}%` }}
                                                                                ></div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Card className="border-0 shadow-sm h-100">
                                                            <Card.Header className="bg-light">
                                                                <h6 className="mb-0 fw-semibold">Highest Revenue Generators</h6>
                                                            </Card.Header>
                                                            <Card.Body>
                                                                {[...couponData]
                                                                    .sort((a, b) => b.salesContribution - a.salesContribution)
                                                                    .slice(0, 3)
                                                                    .map((coupon, index) => (
                                                                        <div key={index} className="mb-2">
                                                                            <div className="d-flex justify-content-between">
                                                                                <span className="fw-semibold">{coupon.couponName}</span>
                                                                                <span className="text-success">{formatCurrency(coupon.salesContribution)}</span>
                                                                            </div>
                                                                            <div className="progress mt-1" style={{ height: '8px' }}>
                                                                                <div 
                                                                                    className="progress-bar bg-primary" 
                                                                                    role="progressbar" 
                                                                                    style={{ width: `${(coupon.salesContribution / getTotalSalesContribution()) * 100}%` }}
                                                                                ></div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div> */}
                                        </Card.Body>
                                        <Card.Footer className="text-muted">
                                            <small>Summary generated on {format(new Date(), 'dd MMMM yyyy hh:mm a')}</small>
                                        </Card.Footer>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default CouponUsageSummary